import React, { FC, ReactElement, useContext, useEffect, useMemo } from 'react'
import {
    CONFIGURATION,
    ConfigurationTabFieldNames,
    CRM_TEMPLATE_TYPE_SCOPE_NAME,
    CRM_TYPE_SCOPE_NAME
} from '../Constants/FieldNames'
import { CRMModal } from '../../../../../../../i18n'
import FormRow from '../../../../../../React/Common/Components/FormRow'
import { CRMTypes, useCRMTypes } from '../../../../../Hooks/useCRMTypes'
import { SelectOption } from '../../../../../../React/Common/Components/Selects/Types'
import CLanguageHandler from '../../../../../../NonReact/Languages/LanguageHandler'
import LanguageContext from '../../../../../../React/Common/Contexts/LanguageContext'
import { ANY_VALUE_STRING_NUMERIC } from '../../../../../../React/Common/Constants/DefaultValues'
import { ConfigurableTableFieldForm } from '../../../../Molecules/ConfigurableTableFieldForm/ConfigurableTableFieldForm'
import { ConfigurableTableConfig, FormTypes } from '../../../../Molecules/ConfigurableTableFieldForm/Types/Types'
import { CRMServerResponse, KeyedCrmConfigEntries, OptionsType, TemplateField } from '../Types/Types'
import { ComboBox } from '../../../../../../React/Common/Components/ComboBox/ComboBox'
import { SelectFilterOption } from '../../../../Molecules/AdminFilters/SelectFilter'
import { isStringNotEmpty, IsVarEmpty, isVarNotEmpty } from '../../../../../../NonReact/utils/GlobalFunctions'
import { CRMTypesResponse } from '../../../../../../NonReact/common/Types/Types'
import { CRMConfigurationTemplateType } from '../Constants/Constants'
import { useFormContext, UseFormMethods } from '../../../../../../Vendor/react-hook-form/dist/index.ie11'
import { OutlinedSelect } from '../../../../Atoms/Inputs/OutlinedSelect'
import { useFeatureFlag } from '../../../../../../React/Common/Hooks/useFeatureFlag'
import { ENABLE_CRM_TEMPLATE_TYPES } from '../../../../../Constants/FeatureFlags'
import { CrmOAuthButton } from '../../../../Molecules/CrmOAuthButton/CrmOAuthButton'
import { OAuthConfig } from '../../../../../../React/Common/Components/OAuthButton/Types/Types'
import { CommonPrefix, ConfigSchemaPrefix, mapConfigValueEntries } from '../Mappers/Mappers'
import { DEFAULT_PASSWORD_VALUE } from '../../../../../../React/AddUser/AddUserMapper'

export type Props = {
    isViewOnly?: boolean
    currentCRMConfiguration?: Nullable<CRMServerResponse>
    selectedCRMTemplate?: Nullable<TemplateField[]>
    authConfiguration?: Nullable<OAuthConfig>
}

export const ConfigurationTab: FC<Props> = ({
    isViewOnly,
    currentCRMConfiguration,
    authConfiguration,
    selectedCRMTemplate
}: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    const [crmTypes]: CRMTypes = useCRMTypes()

    const { watch, setValue }: UseFormMethods = useFormContext()
    const configTemplateType: CRMConfigurationTemplateType = watch(CRM_TEMPLATE_TYPE_SCOPE_NAME)

    const isTemplateTypesEnabled: boolean = useFeatureFlag<boolean>(ENABLE_CRM_TEMPLATE_TYPES, false)

    const currentConfigValues: KeyedCrmConfigEntries = isVarNotEmpty(currentCRMConfiguration)
        ? {
              ...mapConfigValueEntries(currentCRMConfiguration.CRMConfiguration, ConfigSchemaPrefix),
              ...mapConfigValueEntries(currentCRMConfiguration.CRMConfigurationCommon, CommonPrefix)
          }
        : {}

    const mappedCrmTypesToOptions: SelectFilterOption[] =
        crmTypes?.[configTemplateType]
            ?.map(
                (crmType: CRMTypesResponse): SelectFilterOption => ({
                    label: crmType.crmTypeName,
                    value: crmType.crmType
                })
            )
            .sort((a: SelectFilterOption, b: SelectFilterOption): number =>
                a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
            ) ?? []

    const config: Nullable<ConfigurableTableConfig[]> = useMemo(
        (): ConfigurableTableConfig[] =>
            isVarNotEmpty(selectedCRMTemplate)
                ? selectedCRMTemplate
                      // Include Config Schema and Common only
                      .filter(
                          (field: TemplateField): boolean =>
                              (field.name.startsWith('ConfigSchema.') || field.name.startsWith('Common.')) &&
                              Object.values(FormTypes).includes(field.type as FormTypes)
                      )
                      .map((field: TemplateField): ConfigurableTableConfig => {
                          switch (field.type) {
                              case FormTypes.STRING:
                                  return {
                                      name: field.name,
                                      label: field?.label,
                                      type: FormTypes.STRING,
                                      default: (currentConfigValues[field.name] ?? field?.default) as Definable<string>,
                                      validations: field.validations
                                  }

                              case FormTypes.BOOLEAN:
                                  return {
                                      name: field.name,
                                      label: field?.label,
                                      type: FormTypes.BOOLEAN,
                                      default: (currentConfigValues[field.name] ??
                                          field?.default) as Definable<boolean>,
                                      validations: field.validations
                                  }

                              case FormTypes.INTEGER:
                                  return {
                                      name: field.name,
                                      label: field?.label,
                                      type: FormTypes.INTEGER,
                                      default: (currentConfigValues[field.name] ?? field?.default) as Definable<number>,
                                      validations: field.validations
                                  }

                              case FormTypes.NUMBER:
                                  return {
                                      name: field.name,
                                      label: field?.label,
                                      type: FormTypes.NUMBER,
                                      default: (currentConfigValues[field.name] ?? field?.default) as Definable<number>,
                                      validations: field.validations
                                  }

                              case FormTypes.SELECT:
                                  return {
                                      name: field.name,
                                      label: field?.label,
                                      type: FormTypes.SELECT,
                                      options:
                                          field.options?.map(
                                              (option: OptionsType): SelectOption<string> => ({
                                                  label: option.label,
                                                  value: option.value
                                              })
                                          ) ?? [],
                                      default: (currentConfigValues[field.name] ?? field?.default) as Definable<string>,
                                      validations: field.validations
                                  }

                              case FormTypes.ARRAY:
                                  return {
                                      name: field.name,
                                      label: field?.label,
                                      type: FormTypes.ARRAY,
                                      arrayFieldName: field.name.split('.').pop() || field.name,
                                      default:
                                          (currentConfigValues[field.name] as { value: string }[]) ??
                                          ((field?.default as Definable<string[]>) ?? []).map(
                                              (item: string): { value: string } => ({
                                                  value: item
                                              })
                                          ),
                                      validations: field.validations
                                  }

                              case FormTypes.PASSWORD:
                                  return {
                                      name: field.name,
                                      label: field?.label,
                                      type: FormTypes.PASSWORD,
                                      default: (isVarNotEmpty(currentConfigValues[field.name])
                                          ? DEFAULT_PASSWORD_VALUE
                                          : field?.default) as Definable<string>,
                                      validations: field.validations
                                  }

                              case FormTypes.MULTILINE:
                                  return {
                                      name: field.name,
                                      label: field?.label,
                                      type: FormTypes.MULTILINE,
                                      default: (currentConfigValues[field.name] ?? field?.default) as Definable<string>,
                                      validations: field.validations
                                  }

                              default:
                                  throw new Error(`Unsupported field type: ${field.type}`)
                          }
                      })
                : [],
        [JSON.stringify(selectedCRMTemplate)]
    )

    useEffect((): void => {
        // reset the selected crm if the template type changes as the fields between templates could differ
        setValue(CRM_TYPE_SCOPE_NAME, undefined)
    }, [configTemplateType])

    return (
        <div className='tab-area'>
            {isTemplateTypesEnabled ? (
                <FormRow>
                    <OutlinedSelect
                        disabled={isViewOnly || isVarNotEmpty(currentCRMConfiguration)}
                        options={[
                            {
                                label: language.get(CRMModal.CRMConfigurationTab_TemplateType_Desktop),
                                value: CRMConfigurationTemplateType.Desktop
                            },
                            {
                                label: language.get(CRMModal.CRMConfigurationTab_TemplateType_Cloud),
                                value: CRMConfigurationTemplateType.Cloud
                            }
                        ]}
                        name={CRM_TEMPLATE_TYPE_SCOPE_NAME}
                        label={language.get(CRMModal.CRMConfigurationTemplateTypeLabel)}
                    />
                </FormRow>
            ) : (
                <></>
            )}
            <FormRow>
                <ComboBox
                    name={CRM_TYPE_SCOPE_NAME}
                    defaultOption={{
                        label: language.get(CRMModal.CRMConfigurationTypeNone),
                        value: ANY_VALUE_STRING_NUMERIC
                    }}
                    options={mappedCrmTypesToOptions}
                    label={language.get(CRMModal.CRMConfigurationCRMTypeLabel)}
                    variant='outlined'
                    disabled={isViewOnly || isVarNotEmpty(currentCRMConfiguration) || IsVarEmpty(configTemplateType)}
                />
            </FormRow>
            {isVarNotEmpty(authConfiguration) && isVarNotEmpty(currentCRMConfiguration) && (
                <CrmOAuthButton
                    config={authConfiguration}
                    crmConfigId={currentCRMConfiguration.CRMConfigurationID}
                    isAlreadyAuthenticated={isStringNotEmpty(currentCRMConfiguration.OAuthInfo?.access_token)}
                />
            )}
            {isVarNotEmpty(selectedCRMTemplate) &&
            selectedCRMTemplate.length > 0 &&
            isVarNotEmpty(config) &&
            config.length > 0 ? (
                <ConfigurableTableFieldForm
                    namespacingFunction={(name: string): string => `${CONFIGURATION}.${name}`}
                    localisationFunction={(name: string): string => language.get(`CRMIntegration_Options_${name}`)}
                    arrayFieldName={ConfigurationTabFieldNames.CRM_OPTIONS}
                    inputConfig={config}
                    isViewOnly={isViewOnly}
                    localisation={{
                        header: {
                            item: language.get(CRMModal.CRMConfigurationTab_Setting_Name),
                            value: language.get(CRMModal.CRMConfigurationTab_Value)
                        }
                    }}
                    shouldScrollToTheTop={true}
                />
            ) : (
                <></>
            )}
        </div>
    )
}
